import { createZustandContext } from "$/lib/create-zustand-context"
import { StoreIdNotFoundError } from "$/lib/errors"
import storeQueries from "$/lib/queries/store-queries"
import { userQueryOptions } from "$/lib/queries/user-queries"
import { STORE_TIER, STORE_TYPE, StoreTier, StoreType } from "@repo/common"
import { setTag } from "@sentry/react"
import { QueryClient, queryOptions } from "@tanstack/react-query"
import React from "react"
import { createStore, useStore } from "zustand"
import { createJSONStorage, persist } from "zustand/middleware"

export type StoreInfo = {
  name: string
  id: string
  slug: string
  logo: string | null
  type: StoreType
  tier: StoreTier | null
  hasSpecter: boolean
  customBikeTypesEnabled: boolean
  whiteLabelBookingEnabled: boolean
  companiesEnabled: boolean
  customerClubEnabled: boolean
}

type StoreInformationState = {
  user: {
    id: string
    name: string
    email: string
  }
  storeId: string
  stores: StoreInfo[]
  actions: {
    setStoreId: (id: string) => void
    ensureStore: (store: StoreInfo) => void
  }
}

type InitialStoreInformationState = Omit<StoreInformationState, "actions">

export const StoreInformation = createZustandContext<
  InitialStoreInformationState,
  StoreInformationState
>((initialState: InitialStoreInformationState) => {
  return createStore<StoreInformationState>()(
    persist(
      (set) => ({
        ...initialState,
        actions: {
          setStoreId: (id: string) => set({ storeId: id }),
          ensureStore: (store: StoreInfo) =>
            set((state) => ({
              stores: state.stores.some((s) => s.id === store.id)
                ? state.stores
                : [...state.stores, store],
            })),
        },
      }),
      {
        name: "storeId",
        partialize: (state) => state.storeId,
        storage,
      },
    ),
  )
})

function useStoreInformationState<T>(
  selector: (state: StoreInformationState) => T,
) {
  const store = React.useContext(StoreInformation.Context)
  if (!store) {
    throw new Error("Missing StoreInformationProvider")
  }
  return useStore(store, selector)
}

export const useStoreId = (): StoreInformationState["storeId"] =>
  useStoreInformationState((state) => state.storeId)

export const useStoreActions = (): StoreInformationState["actions"] =>
  useStoreInformationState((state) => state.actions)

export const useStores = (): StoreInformationState["stores"] =>
  useStoreInformationState((state) => state.stores)

export const useCurrentStore = (): StoreInformationState["stores"][number] =>
  useStoreInformationState(
    (state) =>
      state.stores.find((store) => store.id === state.storeId) ??
      state.stores[0],
  )

export const useUser = (): StoreInformationState["user"] =>
  useStoreInformationState((state) => state.user)

const storage = createJSONStorage<StoreInformationState["storeId"]>(
  () => localStorage,
)

export async function getStoreId(): Promise<string | null> {
  if (!storage) return null

  const stored = await storage.getItem("storeId")
  if (!stored) return null

  return stored.state
}

export async function setStoreId(storeId: string): Promise<void> {
  if (!storage) return
  await storage.setItem("storeId", { state: storeId })
}

export async function removeStoreId(): Promise<void> {
  if (!storage) return
  await storage.removeItem("storeId")
}

async function getInitialStoreInformation(
  queryClient: QueryClient,
): Promise<InitialStoreInformationState> {
  const [storeId, user] = await Promise.all([
    getStoreId(),
    queryClient.fetchQuery(userQueryOptions),
  ])

  if (!storeId) throw new StoreIdNotFoundError()

  const stores = Object.values(
    user.roles.reduce<Record<string, StoreInfo>>((acc, role) => {
      return Object.assign(acc, {
        [role.storeId]: {
          id: role.storeId,
          name: role.storeName,
          slug: role.storeSlug,
          logo: role.storeLogo,
          type: role.storeType,
          tier: role.storeTier,
          hasSpecter: role.hasSpecter,
          customBikeTypesEnabled: role.customBikeTypesEnabled,
          whiteLabelBookingEnabled: role.whiteLabelBookingEnabled,
          companiesEnabled: role.companiesEnabled,
          customerClubEnabled: role.customerClubEnabled,
        } satisfies StoreInfo,
      })
    }, {}),
  )

  let validStoreId = storeId
  if (!stores.some((s) => s.id === storeId)) {
    try {
      const fetchedStore = await queryClient.fetchQuery(
        storeQueries.detail(storeId),
      )
      stores.push({
        id: fetchedStore.id,
        name: fetchedStore.name,
        slug: fetchedStore.slug,
        logo: null,
        type:
          fetchedStore.type === STORE_TYPE.MOBILE
            ? STORE_TYPE.MOBILE
            : STORE_TYPE.PHYSICAL,
        tier:
          fetchedStore.tier === STORE_TIER.FREE
            ? STORE_TIER.FREE
            : fetchedStore.tier === STORE_TIER.START
              ? STORE_TIER.START
              : fetchedStore.tier === STORE_TIER.ALFRED
                ? STORE_TIER.ALFRED
                : null,
        hasSpecter: false,
        customBikeTypesEnabled: fetchedStore.customBikeTypesEnabled,
        whiteLabelBookingEnabled:
          fetchedStore.featureFlags.whiteLabelBookingEnabled,
        companiesEnabled: fetchedStore.featureFlags.companiesEnabled,
        customerClubEnabled: fetchedStore.featureFlags.customerClubEnabled,
      })
    } catch {
      validStoreId = stores[0].id
      await setStoreId(validStoreId)
    }
  }

  // With the user set in getCurrentUser, this makes a store's events and
  // replays findable in Sentry.
  setTag("storeId", validStoreId)

  return {
    storeId: validStoreId,
    stores,
    user: {
      name: `${user.firstName} ${user.lastName}`,
      email: user.email,
      id: user.id,
    },
  }
}

export const storeInformationQueries = {
  detail: (queryClient: QueryClient) =>
    queryOptions({
      queryKey: storeInformationQueryOptions.queryKey,
      queryFn: () => getInitialStoreInformation(queryClient),
      staleTime: Number.POSITIVE_INFINITY,
    }),
}

const storeInformationQueryOptions = queryOptions({
  queryKey: ["store-information"],
  staleTime: Number.POSITIVE_INFINITY,
})
